import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Plus } from 'lucide-react';
import { StepTransition, Workflow } from '@/types/workflow';

interface TransitionEditorProps {
  transition?: StepTransition;
  onChange: (transition: StepTransition | undefined) => void;
  fieldOptions?: Array<{ value: string; label: string }>;
  availableWorkflows: Workflow[];
}

export const TransitionEditor: React.FC<TransitionEditorProps> = ({
  transition,
  onChange,
  fieldOptions = [],
  availableWorkflows
}) => {
  const handleModeChange = (mode: 'byAnswer' | 'byGuard' | 'always' | 'none') => {
    console.log('[TransitionEditor] Mode changed:', mode);

    if (mode === 'none') {
      onChange(undefined);
      return;
    }

    const newTransition = {
      mode,
      map: mode === 'byAnswer' ? {} : undefined,
      elseIf: mode === 'byGuard' ? [] : undefined,
      default: undefined,
      onOther: undefined
    };

    console.log('[TransitionEditor] Creating new transition:', newTransition);
    onChange(newTransition);
  };

  const updateMap = (answerValue: string, target: string) => {
    if (!transition || transition.mode !== 'byAnswer') return;

    const updatedTransition = {
      ...transition,
      map: {
        ...transition.map,
        [answerValue]: { to: target }
      }
    };

    console.log('[TransitionEditor] Updating map:', {
      answerValue,
      target,
      updatedTransition
    });

    onChange(updatedTransition);
  };

  const removeMapEntry = (answerValue: string) => {
    if (!transition || transition.mode !== 'byAnswer' || !transition.map) return;

    const newMap = { ...transition.map };
    delete newMap[answerValue];

    onChange({
      ...transition,
      map: newMap
    });
  };

  const addElseIf = () => {
    if (!transition || transition.mode !== 'byGuard') return;

    onChange({
      ...transition,
      elseIf: [
        ...(transition.elseIf || []),
        { guard: '', to: '' as any }
      ]
    });
  };

  const updateElseIf = (index: number, field: 'guard' | 'to', value: string) => {
    if (!transition || transition.mode !== 'byGuard' || !transition.elseIf) return;

    const newElseIf = [...transition.elseIf];
    newElseIf[index] = { ...newElseIf[index], [field]: value } as { guard: string; to: any };

    onChange({
      ...transition,
      elseIf: newElseIf
    });
  };

  const removeElseIf = (index: number) => {
    if (!transition || transition.mode !== 'byGuard' || !transition.elseIf) return;

    onChange({
      ...transition,
      elseIf: transition.elseIf.filter((_, i) => i !== index)
    });
  };

  const updateDefault = (target: string) => {
    if (!transition) return;

    onChange({
      ...transition,
      default: target || undefined
    });
  };

  const updateOnOther = (field: string, value: any) => {
    if (!transition) return;

    onChange({
      ...transition,
      onOther: {
        ...transition.onOther,
        [field]: value
      } as any
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transitions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode Selection */}
        <div className="space-y-2">
          <Label>Transition Mode</Label>
          <Select
            value={transition?.mode || 'none'}
            onValueChange={(value: any) => handleModeChange(value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No transitions (sequential)</SelectItem>
              <SelectItem value="byAnswer">Route by answer</SelectItem>
              <SelectItem value="byGuard">Route by condition</SelectItem>
              <SelectItem value="always">Always route</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {transition && (
          <>
            {/* Answer Mapping (byAnswer mode) */}
            {transition.mode === 'byAnswer' && fieldOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Answer Mapping</Label>
                <div className="space-y-2">
                  {fieldOptions.map((option) => (
                    <div key={option.value} className="grid grid-cols-2 gap-2">
                      <Input value={option.label} disabled />
                      <div className="flex gap-2">
                        <Select
                          value={transition.map?.[option.value]?.to as string || '__none__'}
                          onValueChange={(value) => {
                            if (value === '__none__') {
                              removeMapEntry(option.value);
                            } else {
                              updateMap(option.value, value);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select destination" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No transition</SelectItem>
                            {availableWorkflows.map((wf) => (
                              <SelectItem key={wf.id} value={wf.slug}>
                                {wf.title}
                              </SelectItem>
                            ))}
                            <SelectItem value="__complete__">Complete workflow</SelectItem>
                          </SelectContent>
                        </Select>
                        {transition.map?.[option.value] && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMapEntry(option.value)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ElseIf Conditions (byGuard mode) */}
            {transition.mode === 'byGuard' && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Conditions</Label>
                  <Button variant="outline" size="sm" onClick={addElseIf}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Condition
                  </Button>
                </div>
                <div className="space-y-2">
                  {transition.elseIf?.map((condition, idx) => (
                    <Card key={idx}>
                      <CardContent className="pt-4">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Label className="text-sm">Condition {idx + 1}</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeElseIf(idx)}
                              className="h-6 w-6 p-0 text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <Input
                            placeholder="Guard expression (e.g., time.hour >= 18)"
                            value={condition.guard}
                            onChange={(e) => updateElseIf(idx, 'guard', e.target.value)}
                          />
                          <Select
                            value={condition.to as string || '__none__'}
                            onValueChange={(value) => updateElseIf(idx, 'to', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select destination" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Select destination</SelectItem>
                              {availableWorkflows.map((wf) => (
                                <SelectItem key={wf.id} value={wf.slug}>
                                  {wf.title}
                                </SelectItem>
                              ))}
                              <SelectItem value="__complete__">Complete workflow</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Default Destination */}
            <div className="space-y-2">
              <Label>Default Destination</Label>
              <Select
                value={(transition.default as string) || '__none__'}
                onValueChange={(value) => updateDefault(value === '__none__' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select default destination" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (sequential)</SelectItem>
                  {availableWorkflows.map((wf) => (
                    <SelectItem key={wf.id} value={wf.slug}>
                      {wf.title}
                    </SelectItem>
                  ))}
                  <SelectItem value="__complete__">Complete workflow</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Other Transition */}
            {transition.mode === 'byAnswer' && (
              <div className="space-y-2">
                <Label>Handle "Other" Response</Label>
                <Select
                  value={transition.onOther?.mode || 'disabled'}
                  onValueChange={(value) => updateOnOther('mode', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="toWorkflow">Route to workflow</SelectItem>
                    <SelectItem value="toAgent">Route via agent</SelectItem>
                  </SelectContent>
                </Select>

                {transition.onOther?.mode === 'toWorkflow' && (
                  <Select
                    value={(transition.onOther?.to as string) || '__none__'}
                    onValueChange={(value) => updateOnOther('to', value === '__none__' ? undefined : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select workflow" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select workflow</SelectItem>
                      {availableWorkflows.map((wf) => (
                        <SelectItem key={wf.id} value={wf.slug}>
                          {wf.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {transition.onOther?.mode === 'toAgent' && (
                  <Textarea
                    placeholder="Agent prompt template (e.g., User wants: '{{answer}}'...)"
                    value={transition.onOther?.prompt || ''}
                    onChange={(e) => updateOnOther('prompt', e.target.value)}
                    rows={3}
                  />
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
